import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import TranslationLanguagesSupportToggle from "@/v2-backoffice-client/translation/languages/TranslationLanguagesSupportToggle"
import { useTranslationLanguagesQuery } from "@/v2-backoffice-client/translation/languages/translation.languages.query"
import {
  coveragePercent,
  descriptionCoveragePercent,
  type TranslationLanguageRow,
} from "@/v2-backoffice-client/translation/languages/translation.languages.utils"

// The STYLE.md section that defines per-language orthography authorities —
// the gate a language must pass before AI fills produce trustworthy text.
const STYLE_GUIDE_URL =
  "https://github.com/placepoint-no/docs/blob/main/STYLE.md#product-ui-text-localization-follows-the-same-principles"

const LANGUAGE_COLUMNS = createColumns<TranslationLanguageRow>({
  enableSorting: true,
  columns: {
    supported: {
      id: "supported",
      label: "Supported",
      defaultSize: 120,
      getValue: (row) => (row.isSupported ? "Supported" : "Off"),
      renderCell({ row }) {
        return (
          <span className="flex items-center gap-2">
            <Dot
              variant={row.original.isSupported ? "success-1" : "muted"}
              className="shrink-0"
            />
            {row.original.isSupported ? "Supported" : "Off"}
          </span>
        )
      },
    },
    language: {
      id: "language",
      label: "Language",
      defaultSize: 220,
      getValue: (row) => `${row.nativeName} ${row.name} ${row.code}`,
      renderCell({ row }) {
        return (
          <span className="flex min-w-0 flex-col">
            <span className="truncate">
              {row.original.nativeName || row.original.name}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {row.original.code}
              {row.original.name ? ` · ${row.original.name}` : ""}
            </span>
          </span>
        )
      },
    },
    coverage: {
      id: "coverage",
      label: "Coverage",
      defaultSize: 200,
      getValue: (row) => coveragePercent(row),
      renderCell({ row }) {
        const percent = coveragePercent(row.original)
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <span className="text-xs">
              {row.original.translatedCount.toLocaleString("en-GB")} /{" "}
              {row.original.totalCount.toLocaleString("en-GB")} · {percent}%
            </span>
            <span className="h-1.5 w-full max-w-40 overflow-hidden rounded bg-muted">
              <span
                className="block h-full rounded bg-primary"
                style={{ width: `${percent}%` }}
              />
            </span>
          </span>
        )
      },
    },
    missing: {
      id: "missing",
      label: "Missing",
      defaultSize: 100,
      getValue: (row) => row.missingCount,
      renderCell({ row }) {
        if (row.original.missingCount === 0) {
          return <span className="text-muted-foreground">0</span>
        }
        return <span>{row.original.missingCount.toLocaleString("en-GB")}</span>
      },
    },
    descriptions: {
      id: "descriptions",
      label: "Descriptions",
      description:
        "Description coverage, counted apart from the name. Only objects that have a description in some language are counted, so this total is smaller than the name total. A language showing full name coverage can still be missing descriptions, which is exactly how ~140 empty ones per language went unnoticed.",
      defaultSize: 200,
      getValue: (row) => descriptionCoveragePercent(row),
      renderCell({ row }) {
        const percent = descriptionCoveragePercent(row.original)
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <span className="text-xs">
              {row.original.descriptionTranslatedCount.toLocaleString("en-GB")}{" "}
              / {row.original.descriptionExpectedCount.toLocaleString("en-GB")}{" "}
              · {percent}%
            </span>
            <span className="h-1.5 w-full max-w-40 overflow-hidden rounded bg-muted">
              <span
                className="block h-full rounded bg-primary"
                style={{ width: `${percent}%` }}
              />
            </span>
          </span>
        )
      },
    },
    descriptionsMissing: {
      id: "descriptionsMissing",
      label: "Missing descriptions",
      defaultSize: 160,
      getValue: (row) => row.descriptionMissingCount,
      renderCell({ row }) {
        if (row.original.descriptionMissingCount === 0) {
          return <span className="text-muted-foreground">0</span>
        }
        return (
          <span>
            {row.original.descriptionMissingCount.toLocaleString("en-GB")}
          </span>
        )
      },
    },
    style: {
      id: "style",
      label: "STYLE.md",
      description:
        "Whether STYLE.md names this language (its orthography authority + domain sources). Without a section, AI fills are best-effort.",
      defaultSize: 130,
      getValue: (row) => (row.hasStyleSection ? "Has section" : "No section"),
      renderCell({ row }) {
        return (
          <a
            href={STYLE_GUIDE_URL}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "underline-offset-2 hover:underline",
              row.original.hasStyleSection
                ? "text-muted-foreground"
                : "text-destructive",
            )}
          >
            {row.original.hasStyleSection ? "Has section" : "No section"}
          </a>
        )
      },
    },
    actions: {
      id: "actions",
      label: "",
      defaultSize: 110,
      getValue: () => "",
      renderCell({ row }) {
        return <TranslationLanguagesSupportToggle row={row.original} />
      },
    },
  },
})

export type TranslationLanguagesViewProps = Omit<
  TableProps<TranslationLanguageRow>,
  "data" | "columns"
>

// "Languages" (FUS-848): the language registry with coverage and the
// enable/disable toggle. Supported languages are what the editor renders
// fields for, what the AI fills, and what the publish exports carry; the
// user-facing selector stays behind the PostHog staff gate on top.
function TranslationLanguagesView(props: TranslationLanguagesViewProps) {
  const { className, ...tableProps } = props

  const { data, loading, error } = useTranslationLanguagesQuery()
  errorToast(error, { title: "Failed to load languages" })

  const rows: TranslationLanguageRow[] = (
    data?.getTranslationLanguageCoverage ?? []
  ).map((language) => ({
    code: language.Code,
    name: language.Name ?? "",
    nativeName: language.NativeName ?? "",
    isSupported: language.IsSupported,
    totalCount: language.TotalCount,
    translatedCount: language.TranslatedCount,
    missingCount: language.MissingCount,
    descriptionExpectedCount: language.DescriptionExpectedCount,
    descriptionTranslatedCount: language.DescriptionTranslatedCount,
    descriptionMissingCount: language.DescriptionMissingCount,
    hasStyleSection: language.HasStyleSection,
  }))

  return (
    <Table
      className={cn("translation-languages-table", className)}
      tableId="translation-languages-table"
      data={rows}
      columns={LANGUAGE_COLUMNS}
      // Supported languages first, then by coverage.
      initialSorting={[
        { id: "supported", desc: false },
        { id: "coverage", desc: true },
      ]}
      getRowId={(row) => row.code}
      enableRowSelection={false}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.Search placeholder="Search language..." />
        <div className="flex-1" />
        <Table.ToolbarMenu exportFileName="translation-languages" />
      </Table.Toolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default TranslationLanguagesView
